import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Spinner, Alert, Modal, Badge, Form, InputGroup} from 'react-bootstrap';
import api from './api';
import AbonelikEkleFormu from './AbonelikEkleFormu';

function AdminAbonelikler() {
  const [abonelikler, setAbonelikler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [seciliKullanici, setSeciliKullanici] = useState(null);
  
  // Çoklu seçim için state
  const [seciliIdler, setSeciliIdler] = useState(new Set());

  const [aramaMetni, setAramaMetni] = useState('');

  const abonelikleriGetir = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/abonelikler');
      setAbonelikler(response.data);
    } catch (err) {
      setError("Abonelikler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  const filtrelenmisAbonelikler = abonelikler.filter(abone => {
    const arama = aramaMetni.toLowerCase();
    const kullaniciAdiMatch = abone.kullanici_adi.toLowerCase().includes(arama);
    const klinikAdiMatch = abone.klinik_adi.toLowerCase().includes(arama);
    return kullaniciAdiMatch || klinikAdiMatch;
  });

  useEffect(() => {
    abonelikleriGetir();
  }, [abonelikleriGetir]);

  // Checkbox değişimlerini yöneten fonksiyon
  const handleSecimChange = (kullaniciId) => {
    const newSeciliIdler = new Set(seciliIdler);
    if (newSeciliIdler.has(kullaniciId)) {
      newSeciliIdler.delete(kullaniciId);
    } else {
      newSeciliIdler.add(kullaniciId);
    }
    setSeciliIdler(newSeciliIdler);
  };

  // Tümünü seçme/bırakma fonksiyonu
  const handleHepsiniSec = (e) => {
    if (e.target.checked) {
      // Sadece o an ekranda görünen (filtrelenmiş) olanları seç
      setSeciliIdler(new Set(filtrelenmisAbonelikler.map(a => a.kullanici_id)));
    } else {
      setSeciliIdler(new Set());
    }
  };

  const handleModalOpen = (kullanici = null) => {
    // Eğer tek bir kullanıcı için buton tıklandıysa, onu seçili kullanıcı yap
    if (kullanici) {
      setSeciliKullanici(kullanici);
    }
    setShowModal(true);
  };

  const handleModalClose = () => {
    setSeciliKullanici(null);
    setShowModal(false);
  };

  const handleFormSubmit = async (formData) => {
    // Eğer çoklu seçim yapıldıysa, toplu güncelleme modunda çalış
    const isBulkUpdate = seciliIdler.size > 0;
    
    const url = isBulkUpdate ? '/admin/abonelikler/toplu-uzat' : '/admin/abonelikler';
    
    const payload = isBulkUpdate 
      ? { ...formData, kullanici_idler: Array.from(seciliIdler) }
      : { ...formData, kullanici_id: seciliKullanici.kullanici_id };

    try {
      await api.post(url, payload);
      handleModalClose();
      setSeciliIdler(new Set()); // Başarılı işlem sonrası seçimleri temizle
      abonelikleriGetir(); // Listeyi yenile
    } catch (err) {
      alert(`Hata: ${err.response?.data?.mesaj || 'İşlem başarısız'}`);
    }
  };

  // Kalan güne göre renkli etiket (badge) döndüren fonksiyon
  const getKalanGunBadge = (kalanGun) => {
    if (kalanGun < 0) return <Badge bg="danger">Süresi Doldu</Badge>;
    if (kalanGun <= 15) return <Badge bg="warning">{kalanGun} gün kaldı</Badge>;
    return <Badge bg="success">{kalanGun} gün kaldı</Badge>;
  };
  
  if (loading) return <Spinner animation="border" variant="light" />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <>
      <Card className="glass-card text-white">
        <Card.Header as="h2" className="d-flex justify-content-between align-items-center">
          <span>Abonelik Yönetimi</span>
          {seciliIdler.size > 0 && (
            <Button variant="warning" onClick={() => handleModalOpen()}>
              {seciliIdler.size} Seçili Kullanıcı İçin Süre Ekle
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          {/* YENİ EKLENDİ: Arama Çubuğu */}
          <InputGroup className="mb-3">
            <Form.Control
              placeholder="Diyetisyen veya klinik adına göre filtrele..."
              value={aramaMetni}
              onChange={(e) => setAramaMetni(e.target.value)}
            />
          </InputGroup>

          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '5%' }}>
                    <Form.Check
                      type="checkbox"
                      title="Görünenlerin Tümünü Seç"
                      onChange={handleHepsiniSec}
                      // GÜNCELLENDİ: Checkbox durumu filtrelenmiş listeye göre belirleniyor
                      checked={filtrelenmisAbonelikler.length > 0 && seciliIdler.size === filtrelenmisAbonelikler.length}
                    />
                  </th>
                  <th>Diyetisyen Adı</th>
                  <th>Klinik Adı</th>
                  <th>Abonelik Bitiş Tarihi</th>
                  <th>Durum / Kalan Süre</th>
                  <th className="text-center">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {/* GÜNCELLENDİ: Filtrelenmiş liste ekranda gösteriliyor */}
                {filtrelenmisAbonelikler.map(abone => (
                  <tr key={abone.kullanici_id}>
                    <td data-label="Seç">
                      <Form.Check
                        type="checkbox"
                        checked={seciliIdler.has(abone.kullanici_id)}
                        onChange={() => handleSecimChange(abone.kullanici_id)}
                      />
                    </td>
                    <td data-label="Diyetisyen Adı">{abone.kullanici_adi}</td>
                    <td data-label="Klinik Adı">{abone.klinik_adi}</td>
                    <td data-label="Abonelik Bitiş">{abone.abonelik_bitis_tarihi ? new Date(abone.abonelik_bitis_tarihi).toLocaleDateString('tr-TR') : 'Yok'}</td>
                    <td data-label="Durum">{getKalanGunBadge(abone.kalan_gun)}</td>
                    <td data-label="İşlemler" className="text-center">
                      <Button variant="info" size="sm" onClick={() => handleModalOpen(abone)}>
                        + Süre Ekle
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Abonelik Süresi Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(seciliKullanici || seciliIdler.size > 0) && (
            <AbonelikEkleFormu 
              kullaniciAdi={seciliIdler.size > 0 ? `${seciliIdler.size} diyetisyen` : seciliKullanici?.kullanici_adi}
              onFormSubmit={handleFormSubmit}
              onIptal={handleModalClose}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
export default AdminAbonelikler;